/* Footcourt — installment plans block (blocks/_product-installments.liquid)
 * Reads provider configs rendered by blocks/_installment-provider.liquid,
 * quotes every plan for the current variant price and animates the UI.
 * No dependencies. Theme fires `variant:change` on the product form (Kalles 5).
 *
 * Cart copies (<fc-cart-installments>, snippets/fc-cart-installments.liquid) clone the product
 * widget and quote the cart total instead, so providers/rates/texts are edited in one place. */
(() => {
  if (customElements.get('fc-installments')) return;

  /* ---- shared by the product widget and its cart copies ---- */
  const CART_STATE = new Map(); /* context -> {from, months}: keeps a cart copy steady across cart re-renders */
  let PRISTINE = null; /* {html, section, t}: the product widget exactly as Liquid printed it */
  const tplKey = () => {
    const S = window.Shopify || {};
    return `fcInst:tpl:${(S.theme && S.theme.id) || ''}:${S.locale || document.documentElement.lang || ''}`;
  };
  function remember(el) {
    PRISTINE = { html: el.outerHTML, section: el.dataset.section || '', t: Date.now() };
    try { localStorage.setItem(tplKey(), JSON.stringify(PRISTINE)); } catch (e) { /* private mode / quota: memory copy still works */ }
    queueMicrotask(() => document.dispatchEvent(new CustomEvent('fc-installments:template')));
  }
  function readCache() {
    try {
      const v = JSON.parse(localStorage.getItem(tplKey()) || 'null');
      return v && typeof v.html === 'string' && v.html.indexOf('<fc-installments') === 0 ? v : null;
    } catch (e) { return null; }
  }

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);

  /* "3:0, 6:1.5+4, 12:2%" -> [{n:3, rate:0}, {n:6, rate:1.5, fee:4}, {n:12, rate:2}]
   * months:rate — rate in % (per month, or total; see provider.method)
   * "+4" = one-time admin fee of 4 % for that plan only; "+250egp" (or "+250le") = fixed fee in pounds for that plan */
  function parsePlans(text) {
    const out = [];
    String(text || '')
      .split(/[,\n|;]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((item) => {
        const m = item.match(/^(\d+)\s*(?:m|mo|months?|شهر|شهور|اشهر|أشهر)?\s*(?:[:=@xX×]\s*([\d.]+)\s*%?)?\s*(?:\+\s*([\d.]+)\s*(%|egp|le|e|ج|جنيه|ج\.م)?)?$/i);
        if (!m) return;
        const n = parseInt(m[1], 10);
        if (!n) return;
        const plan = { n, rate: m[2] ? parseFloat(m[2]) : 0 };
        if (m[3] !== undefined) {
          if (m[4] && m[4] !== '%') plan.fee_fixed = parseFloat(m[3]); else plan.fee = parseFloat(m[3]);
        }
        out.push(plan);
      });
    const seen = new Set();
    return out.filter((p) => (seen.has(p.n) ? false : seen.add(p.n))).sort((a, b) => a.n - b.n);
  }

  /* Money: cents -> string via Shopify money format ("LE {{amount}}") */
  function formatMoney(cents, format, decimals) {
    const value = cents / 100;
    const fixed = decimals === 0 ? Math.round(value) : value;
    const withSep = (num, thousands, decimal, places) => {
      const parts = Math.abs(num).toFixed(places).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return (num < 0 ? '-' : '') + (places ? parts.join(decimal) : parts[0]);
    };
    let text;
    const f = format || 'LE {{amount}}';
    const key = (f.match(/\{\{\s*(\w+)\s*\}\}/) || [])[1] || 'amount';
    switch (key) {
      case 'amount_no_decimals': text = withSep(fixed, ',', '.', 0); break;
      case 'amount_with_comma_separator': text = withSep(fixed, '.', ',', decimals); break;
      case 'amount_no_decimals_with_comma_separator': text = withSep(fixed, '.', ',', 0); break;
      case 'amount_with_space_separator': text = withSep(fixed, ' ', ',', decimals); break;
      case 'amount_no_decimals_with_space_separator': text = withSep(fixed, ' ', ',', 0); break;
      default: text = withSep(fixed, ',', '.', decimals);
    }
    return f.replace(/\{\{\s*\w+\s*\}\}/, text).trim();
  }

  /* One plan quote. All money in cents. */
  function quote(p, priceCents, plan, rounding) {
    const P = priceCents;
    const down = Math.round((P * (p.down || 0)) / 100);
    const financed = P - down;
    const feePct = typeof plan.fee === 'number' ? plan.fee : (p.fee_pct || 0);
    const feeFixed = typeof plan.fee_fixed === 'number' ? plan.fee_fixed : (p.fee_fixed || 0);
    const fees = Math.round((P * feePct) / 100) + Math.round(feeFixed * 100);
    const n = plan.n;
    const r = (plan.rate || 0) / 100;
    let interest = 0;
    if (p.method === 'reducing') {
      const pay = r > 0 ? (financed * r) / (1 - Math.pow(1 + r, -n)) : financed / n;
      interest = pay * n - financed;
    } else if (p.method === 'total') {
      interest = financed * r;
    } else {
      interest = financed * r * n; /* flat: on the financed amount, every month */
    }
    let upfront = down;
    let spread = financed + interest;
    if (p.fee_mode === 'upfront') upfront += fees; else spread += fees;
    const raw = spread / n;
    const step = 100; /* round to whole pounds */
    const monthly = rounding === 'round' ? Math.round(raw / step) * step
      : rounding === 'exact' ? Math.round(raw) : Math.ceil(raw / step - 1e-9) * step;
    const total = monthly * n + upfront;
    return { n, rate: plan.rate || 0, feePct, monthly, total, upfront, down, fees, interest: Math.round(interest), extra: total - P };
  }

  /* FLIP reorder animation for a container's children */
  function flip(container, mutate) {
    const kids = [...container.children];
    if (reduceMotion()) { mutate(); return; }
    const before = new Map(kids.map((k) => [k, k.getBoundingClientRect()]));
    mutate();
    kids.forEach((k) => {
      const b = before.get(k); const a = k.getBoundingClientRect();
      const dx = b.left - a.left, dy = b.top - a.top;
      if (!dx && !dy) return;
      k.style.transition = 'none';
      k.style.transform = `translate(${dx}px, ${dy}px)`;
      k.getBoundingClientRect();
      k.style.transition = 'transform .45s cubic-bezier(.2,.8,.2,1)';
      k.style.transform = '';
      k.addEventListener('transitionend', () => { k.style.transition = ''; }, { once: true });
    });
  }

  class FcInstallments extends HTMLElement {
    connectedCallback() {
      this.cart = this.dataset.mode === 'cart';
      if (!this.cart && !this.seen) remember(this); /* before any change: cart copies start from this markup */
      this.seen = true;
      this.cfg = JSON.parse(this.dataset.config || '{}');
      this.price = parseInt(this.dataset.price || '0', 10);
      this.tweens = new WeakMap();
      this.abort = new AbortController();
      this.panel = this.querySelector('[data-fc-panel]');
      this.dialog = this.querySelector('dialog');
      this.grid = this.querySelector('[data-fc-grid]');
      this.chips = this.querySelector('[data-fc-months]');
      this.providers = this.collect();
      this.ctx = this.cart ? (this.dataset.context || 'cart') : '';
      const mem = this.cart ? CART_STATE.get(this.ctx) : null;
      this.months = (mem && mem.months) || parseInt(this.cfg.default_months || '0', 10) || 0;
      this.filter = parsePlans(this.cfg.months_filter || '').map((x) => x.n);
      this.buildLogos();
      this.bind();
      if (mem) {
        /* the cart re-rendered (quantity / remove / discount): no entrance, the amount glides from the old total */
        this.classList.add('is-visible', 'is-steady');
        const from = this.querySelector('[data-fc-from]');
        if (from && mem.from) { from.dataset.v = String(mem.from); from.textContent = formatMoney(mem.from, this.cfg.money, 0); }
        this.update(true);
      } else {
        this.update(false);
        this.reveal();
      }
      this.marquee();
    }

    /* teaser logo strip: cloned from each provider card's <template> (a parent block cannot read child settings in Liquid) */
    buildLogos() {
      const track = this.querySelector('[data-fc-track]');
      if (!track) return;
      track.innerHTML = '';
      let i = 0;
      this.providers.forEach((p) => {
        const tpl = p.el.querySelector('template[data-fc-logo-tpl]');
        if (!tpl) return;
        const chip = tpl.content.firstElementChild.cloneNode(true);
        chip.style.setProperty('--i', String(i++));
        track.appendChild(chip);
        p.logos = [chip];
      });
      const strip = this.querySelector('[data-fc-logos]');
      if (strip) strip.hidden = this.cfg.show_logos === false || !i;
    }
    disconnectedCallback() {
      this.abort.abort();
      /* removed while its popup was open (the theme rebuilds the cart HTML on every change) */
      if (this.dialog && this.dialog.open) { this.dialog.close(); document.documentElement.classList.remove('fc-inst-lock'); }
    }

    collect() {
      return [...this.querySelectorAll('[data-fc-provider]')].map((el) => {
        let p = {};
        try { p = JSON.parse(el.getAttribute('data-fc-provider') || '{}'); } catch (e) { p = {}; }
        p.el = el;
        p.plans = parsePlans(p.plans);
        p.logos = [];
        return p;
      }).filter((p) => p.enabled !== false && p.plans.length);
    }

    bind() {
      const sig = { signal: this.abort.signal };
      /* price follows the variant picker (Kalles: CustomEvent "variant:change" on the form);
       * a cart copy has a fixed price: the cart total it was rendered with */
      if (!this.cart) {
        const form = document.getElementById(this.dataset.form || '') || this.closest('form');
        const onVariant = (e) => {
          const v = e.detail && e.detail.variant;
          if (v && typeof v.price === 'number') { this.price = v.price; this.update(true); }
        };
        if (form) form.addEventListener('variant:change', onVariant, sig);
        document.addEventListener('variant:change', (e) => {
          if (form && e.target === form) return; /* already handled */
          if (!form || (form && form.contains(e.target)) || e.target === document) onVariant(e);
        }, sig);
      }

      /* open / close */
      this.querySelectorAll('[data-fc-open]').forEach((b) => b.addEventListener('click', () => this.toggle(), sig));
      this.querySelectorAll('[data-fc-close]').forEach((b) => b.addEventListener('click', () => this.close(), sig));
      if (this.dialog) {
        /* backdrop click. stopPropagation: inside the cart drawer the theme closes the drawer
         * whenever a click on ANY <dialog> lands outside the drawer's rectangle */
        this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) { e.stopPropagation(); this.close(); } }, sig);
        this.dialog.addEventListener('close', () => this.afterClose(), sig);
        this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); }, sig);
      }
      /* months chips (delegated) */
      if (this.chips) this.chips.addEventListener('click', (e) => {
        const b = e.target.closest('[data-fc-month]');
        if (!b) return;
        this.months = parseInt(b.dataset.fcMonth, 10);
        this.update(true);
      }, sig);
      /* details toggles + keyboard */
      this.grid.addEventListener('click', (e) => {
        const b = e.target.closest('[data-fc-more]');
        if (b) this.toggleCard(b.closest('[data-fc-provider]'));
      }, sig);
      /* theme editor */
      document.addEventListener('shopify:block:select', (e) => {
        if (!this.contains(e.target)) return;
        this.open();
        const card = e.target.closest('[data-fc-provider]');
        if (card) this.toggleCard(card, true);
      }, sig);
      document.addEventListener('shopify:block:deselect', (e) => {
        if (this.contains(e.target) && this.cfg.layout === 'popup') this.close();
      }, sig);
    }

    /* ---- state -> DOM ---- */
    eligible() {
      return this.providers.filter((p) => (!p.min || this.price >= p.min * 100) && (!p.max || this.price <= p.max * 100));
    }

    update(animate) {
      const live = this.eligible();
      let tenures = [...new Set(live.flatMap((p) => p.plans.map((x) => x.n)))].sort((a, b) => a - b);
      if (this.filter.length) tenures = tenures.filter((n) => this.filter.includes(n));
      const nothing = !live.length || !tenures.length || this.price <= 0 || (this.cfg.min_price && this.price < this.cfg.min_price);
      const hint = this.querySelector('[data-fc-hint]');
      if (hint) hint.hidden = !nothing;
      this.hidden = nothing && !hint;
      this.classList.toggle('is-empty', nothing);
      if (nothing) return;
      if (!tenures.includes(this.months)) {
        const pref = parseInt(this.cfg.default_months || '0', 10);
        this.months = tenures.includes(pref) ? pref : tenures.reduce((best, n) => (Math.abs(n - pref) < Math.abs(best - pref) ? n : best), tenures[0]);
      }
      this.renderChips(tenures);
      this.renderTeaser(live, animate);
      this.renderCards(live, animate);
    }

    renderChips(tenures) {
      if (!this.chips) return;
      const html = tenures.map((n) => `<button type="button" class="fc-inst__chip" role="radio" aria-checked="${n === this.months}" data-fc-month="${n}"><b>${n}</b><span>${this.t(n === 1 ? 'month' : 'months')}</span></button>`).join('');
      if (this.chips.dataset.sig !== tenures.join(',')) { this.chips.innerHTML = html; this.chips.dataset.sig = tenures.join(','); }
      else this.chips.querySelectorAll('[data-fc-month]').forEach((b) => b.setAttribute('aria-checked', String(parseInt(b.dataset.fcMonth, 10) === this.months)));
      const active = this.chips.querySelector('[aria-checked="true"]');
      if (active && active.scrollIntoView) active.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduceMotion() ? 'auto' : 'smooth' });
    }

    best(live, n) {
      /* lowest monthly across providers; n = null -> any tenure */
      let bestQ = null, bestP = null;
      live.forEach((p) => p.plans.forEach((plan) => {
        if (n && plan.n !== n) return;
        const q = quote(p, this.price, plan, this.cfg.rounding);
        if (!bestQ || q.monthly < bestQ.monthly) { bestQ = q; bestP = p; }
      }));
      return { q: bestQ, p: bestP };
    }

    renderTeaser(live, animate) {
      const basis = this.cfg.teaser_basis === 'default' ? this.months : null;
      const { q, p } = this.best(live, basis);
      const from = this.querySelector('[data-fc-from]');
      if (q && from) this.tween(from, q.monthly, animate);
      if (q && this.ctx) CART_STATE.set(this.ctx, { from: q.monthly, months: this.months });
      const meta = this.querySelector('[data-fc-teaser-meta]');
      if (meta) {
        const maxN = Math.max(...live.flatMap((x) => x.plans.map((y) => y.n)));
        meta.textContent = basis
          ? this.t('teaser_at').replace('{n}', basis).replace('{provider}', p ? p.name : '')
          : this.t('teaser_upto').replace('{n}', maxN);
      }
      const count = this.querySelector('[data-fc-count]');
      if (count) count.textContent = String(live.length);
      /* logo strip: hide providers not eligible at this price */
      this.providers.forEach((pr) => pr.logos.forEach((el) => { el.hidden = !live.includes(pr); }));
      const pt = this.querySelector('[data-fc-price]');
      if (pt) pt.textContent = formatMoney(this.price, this.cfg.money, 2);
    }

    renderCards(live, animate) {
      const n = this.months;
      const rows = this.providers.map((p) => {
        const on = live.includes(p);
        const plan = on ? p.plans.find((x) => x.n === n) : null;
        return { p, on, plan, q: plan ? quote(p, this.price, plan, this.cfg.rounding) : null };
      });
      const avail = rows.filter((r) => r.q);
      const minMonthly = avail.length ? Math.min(...avail.map((r) => r.q.monthly)) : null;
      rows.forEach((r) => this.fillCard(r, n, minMonthly, animate));
      /* order: available (by monthly asc if sorting on) first, then unavailable, hidden last */
      const order = rows.slice().sort((a, b) => {
        const ra = a.q ? 0 : a.on ? 1 : 2, rb = b.q ? 0 : b.on ? 1 : 2;
        if (ra !== rb) return ra - rb;
        if (a.q && b.q && this.cfg.sort === 'monthly') return a.q.monthly - b.q.monthly;
        return 0;
      });
      const same = order.every((r, i) => this.grid.children[i] === r.p.el);
      if (!same) flip(this.grid, () => order.forEach((r) => this.grid.appendChild(r.p.el)));
      const empty = this.querySelector('[data-fc-empty]');
      if (empty) empty.hidden = avail.length > 0;
    }

    fillCard(r, n, minMonthly, animate) {
      const { p, on, q } = r;
      const el = p.el;
      el.hidden = !on;
      el.classList.toggle('is-off', on && !q);
      el.classList.toggle('is-best', !!q && q.monthly === minMonthly && this.cfg.show_best !== false);
      const badge = el.querySelector('[data-fc-badge]');
      if (badge) { badge.hidden = !el.classList.contains('is-best'); badge.textContent = this.t('lowest'); }
      const monthly = el.querySelector('[data-fc-monthly]');
      const per = el.querySelector('[data-fc-per]');
      const meta = el.querySelector('[data-fc-meta]');
      const more = el.querySelector('[data-fc-more]');
      if (!q) {
        if (monthly) { monthly.textContent = '—'; delete monthly.dataset.v; }
        if (per) per.textContent = this.t('not_available').replace('{n}', n);
        if (meta) meta.innerHTML = '';
        if (more) { more.hidden = true; }
        this.toggleCard(el, false);
        return;
      }
      if (monthly) { monthly.setAttribute('dir', 'ltr'); this.tween(monthly, q.monthly, animate); }
      if (per) per.textContent = this.t('per_month').replace('{n}', n);
      if (meta) meta.innerHTML = this.metaLines(q, p).map((s) => `<span>${s}</span>`).join('');
      if (more) { more.hidden = false; more.firstElementChild && (more.firstElementChild.textContent = this.t(el.classList.contains('is-open') ? 'less' : 'details')); }
      const det = el.querySelector('[data-fc-details]');
      if (det) det.innerHTML = this.detailsHtml(p, n);
    }

    metaLines(q, p) {
      const m = this.cfg.money, lines = [];
      if (q.rate === 0 && q.fees === 0) lines.push(`<em class="fc-inst__free">${this.t('interest_free')}</em>`);
      else if (q.rate === 0) lines.push(`<em class="fc-inst__free">${this.t('interest_free')}</em>`);
      else lines.push(this.t(p.method === 'total' ? 'rate_total' : 'rate_monthly').replace('{rate}', this.num(q.rate)));
      if (q.fees) lines.push(this.t('fees_line').replace('{amount}', formatMoney(q.fees, m, 0)));
      if (q.down) lines.push(this.t('down_line').replace('{amount}', formatMoney(q.down, m, 0)));
      lines.push(this.t('total_line').replace('{amount}', formatMoney(q.total, m, 0)));
      return lines;
    }

    detailsHtml(p, current) {
      const m = this.cfg.money;
      const rows = p.plans.map((plan) => {
        const q = quote(p, this.price, plan, this.cfg.rounding);
        const extra = q.extra > 0 ? `+${formatMoney(q.extra, m, 0)}` : this.t('no_extra');
        return `<tr${plan.n === current ? ' class="is-current"' : ''}><td>${plan.n} ${this.t(plan.n === 1 ? 'month' : 'months')}</td><td><b dir="ltr">${formatMoney(q.monthly, m, 0)}</b></td><td dir="ltr">${formatMoney(q.total, m, 0)}</td><td dir="ltr">${extra}</td></tr>`;
      }).join('');
      const notes = [];
      if (p.down) notes.push(this.t('down_note').replace('{pct}', this.num(p.down)));
      const feeOf = (x) => {
        const pct = typeof x.fee === 'number' ? x.fee : (p.fee_pct || 0);
        const fix = typeof x.fee_fixed === 'number' ? x.fee_fixed : (p.fee_fixed || 0);
        const parts = [];
        if (pct) parts.push(`${this.num(pct)}%`);
        if (fix) parts.push(formatMoney(fix * 100, m, 0));
        return parts.join(' + ');
      };
      const feeTexts = p.plans.map(feeOf);
      if (feeTexts.some(Boolean)) {
        const uniq = [...new Set(feeTexts)];
        const text = uniq.length === 1 ? uniq[0] : p.plans.map((x, i) => `${x.n}${this.t('m_short')}: ${feeTexts[i] || '0'}`).join(', ');
        notes.push(this.t(p.fee_mode === 'upfront' ? 'fee_note_upfront' : 'fee_note_spread').replace('{fee}', text));
      }
      if (p.note) notes.push(p.note);
      const how = p.how ? `<div class="fc-inst__how"><b>${this.t('how_to')}</b><div>${p.how}</div></div>` : '';
      const link = p.url ? `<a class="fc-inst__link" href="${p.url}" target="_blank" rel="noopener">${this.t('learn_more')} ↗</a>` : '';
      return `<table class="fc-inst__table"><thead><tr><th>${this.t('months_th')}</th><th>${this.t('monthly_th')}</th><th>${this.t('total_th')}</th><th>${this.t('extra_th')}</th></tr></thead><tbody>${rows}</tbody></table>`
        + (notes.length ? `<ul class="fc-inst__notes">${notes.map((s) => `<li>${s}</li>`).join('')}</ul>` : '')
        + how + link;
    }

    toggleCard(card, force) {
      if (!card) return;
      const open = typeof force === 'boolean' ? force : !card.classList.contains('is-open');
      if (open) this.grid.querySelectorAll('.is-open').forEach((c) => { if (c !== card) this.toggleCard(c, false); });
      flip(this.grid, () => {
        card.classList.toggle('is-open', open);
        const more = card.querySelector('[data-fc-more]');
        if (more) { more.setAttribute('aria-expanded', String(open)); more.firstElementChild && (more.firstElementChild.textContent = this.t(open ? 'less' : 'details')); }
      });
      if (open && !reduceMotion()) setTimeout(() => card.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 80);
    }

    /* ---- open / close ---- */
    get isOpen() { return this.dialog ? this.dialog.open : this.classList.contains('is-expanded'); }
    toggle() { this.isOpen ? this.close() : this.open(); }
    open() {
      if (this.cfg.layout === 'open') return;
      if (this.dialog) {
        if (this.dialog.open) return;
        this.dialog.showModal();
        document.documentElement.classList.add('fc-inst-lock');
        requestAnimationFrame(() => this.dialog.classList.add('is-in'));
      } else {
        this.classList.add('is-expanded');
      }
      this.querySelectorAll('[data-fc-open]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
      this.update(false);
    }
    close() {
      if (this.cfg.layout === 'open') return;
      if (this.dialog) {
        if (!this.dialog.open) return;
        this.dialog.classList.remove('is-in');
        const done = () => { if (this.dialog.open) this.dialog.close(); };
        reduceMotion() ? done() : setTimeout(done, 260);
      } else {
        this.classList.remove('is-expanded');
      }
      this.querySelectorAll('[data-fc-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    }
    afterClose() { document.documentElement.classList.remove('fc-inst-lock'); }

    /* ---- helpers ---- */
    t(key) { return (this.cfg.strings && this.cfg.strings[key]) || key; }
    num(x) { return (Math.round(x * 100) / 100).toString(); }
    tween(el, toCents, animate) {
      const from = parseInt(el.dataset.v || '0', 10);
      el.dataset.v = String(toCents);
      const m = this.cfg.money;
      if (!animate || reduceMotion() || !from) { el.textContent = formatMoney(toCents, m, 0); return; }
      const start = performance.now(), dur = 480;
      const prev = this.tweens.get(el); if (prev) cancelAnimationFrame(prev);
      const step = (now) => {
        const t = Math.min(1, (now - start) / dur);
        el.textContent = formatMoney(Math.round(from + (toCents - from) * easeOut(t)), m, 0);
        if (t < 1) this.tweens.set(el, requestAnimationFrame(step));
      };
      this.tweens.set(el, requestAnimationFrame(step));
    }
    marquee() {
      const strip = this.querySelector('[data-fc-logos]');
      const track = strip && strip.querySelector('.fc-inst__track');
      if (!strip || !track || this.cfg.logos === 'wrap') return;
      const fit = () => {
        const w = track.scrollWidth;
        const over = w > strip.clientWidth + 2;
        if (over && !strip.classList.contains('is-marquee')) {
          const clone = track.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          strip.appendChild(clone);
          strip.classList.add('is-marquee');
          this.providers.forEach((p) => { p.logos = [...this.querySelectorAll(`[data-fc-logo-for="${p.id}"]`)]; });
        } else if (!over && strip.classList.contains('is-marquee')) {
          strip.classList.remove('is-marquee');
          [...strip.querySelectorAll('.fc-inst__track')].slice(1).forEach((c) => c.remove());
          this.providers.forEach((p) => { p.logos = [...this.querySelectorAll(`[data-fc-logo-for="${p.id}"]`)]; });
        }
        if (strip.classList.contains('is-marquee')) {
          strip.style.setProperty('--fc-marquee-w', `${w + 6}px`);
          strip.style.setProperty('--fc-marquee-dur', `${Math.max(12, Math.round(w / 24))}s`);
        }
      };
      const run = () => { fit(); };
      if (document.readyState === 'complete') run(); else window.addEventListener('load', run, { once: true, signal: this.abort.signal });
      const imgs = [...track.querySelectorAll('img')];
      imgs.forEach((im) => { if (!im.complete) im.addEventListener('load', run, { once: true, signal: this.abort.signal }); });
      if ('ResizeObserver' in window) { const ro = new ResizeObserver(() => fit()); ro.observe(strip); this.abort.signal.addEventListener('abort', () => ro.disconnect()); }
      run();
    }
    reveal() {
      if (reduceMotion() || !('IntersectionObserver' in window)) { this.classList.add('is-visible'); return; }
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { this.classList.add('is-visible'); io.disconnect(); }
      }, { threshold: 0.15 });
      io.observe(this);
    }
  }
  customElements.define('fc-installments', FcInstallments);

  /* ---------- cart copy: <fc-cart-installments> ----------
   * Rendered by the "Installments" block of the cart drawer and the cart page
   * (snippets/fc-cart-installments.liquid). It clones the product widget, so providers, rates,
   * logos, texts and colours are edited once, in the product page block, and quotes cart.total_price.
   * Template source, in order: the widget printed on this page, the browser's copy saved by the last
   * product page, the first cart item's product page. Kalles rebuilds the cart HTML on every change,
   * so each change brings a fresh host with the new total; CART_STATE makes it read as one widget. */
  const FRESH_MS = 15 * 60 * 1000;
  let FETCHING = null;
  const signature = (html) => (String(html).match(/(?:data-config|data-fc-provider|style|src)="[^"]*"/g) || []).join('|');
  const esc = (v) => String(v).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const SVG_CHEVRON = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 3l5 5-5 5"/></svg>';
  const SVG_CLOSE = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13"/></svg>';

  function fetchTemplate(handle, section) {
    if (FETCHING) return FETCHING;
    const S = window.Shopify || {};
    const base = `${(S.routes && S.routes.root) || '/'}products/${encodeURIComponent(handle)}`;
    /* the section alone is ~6x lighter, but its id is only known from an earlier product page */
    const urls = section ? [`${base}?section_id=${encodeURIComponent(section)}`, base] : [base];
    FETCHING = (async () => {
      for (const url of urls) {
        try {
          const res = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'text/html' } });
          if (!res.ok) continue;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          const el = doc.querySelector('fc-installments:not([data-mode="cart"])');
          if (!el) continue;
          const rec = { html: el.outerHTML, section: el.getAttribute('data-section') || '', t: Date.now() };
          try { localStorage.setItem(tplKey(), JSON.stringify(rec)); } catch (e) { /* ignore */ }
          return rec;
        } catch (e) { /* offline / blocked: keep what we have */ }
      }
      return null;
    })();
    FETCHING.finally(() => { FETCHING = null; });
    return FETCHING;
  }

  /* rebuild the wrappers of a cloned widget for another layout: popup | inline | open */
  function relayout(el, to, strings, closeLabel) {
    const doc = el.ownerDocument;
    const panel = el.querySelector('.fc-inst__panel');
    let teaser = el.querySelector('.fc-inst__teaser');
    if (!panel || !teaser) return;
    const holder = panel.closest('dialog, .fc-inst__wrap');
    if (holder) holder.replaceWith(panel);
    const wantButton = to !== 'open';
    if (wantButton !== (teaser.tagName === 'BUTTON')) {
      const t = doc.createElement(wantButton ? 'button' : 'div');
      t.className = 'fc-inst__teaser';
      if (wantButton) { t.setAttribute('type', 'button'); t.setAttribute('data-fc-open', ''); t.setAttribute('aria-expanded', 'false'); }
      t.append(...teaser.childNodes);
      teaser.replaceWith(t);
      teaser = t;
    }
    const cta = teaser.querySelector('.fc-inst__cta');
    if (!wantButton && cta) cta.remove();
    const row = teaser.querySelector('.fc-inst__row');
    if (wantButton && !cta && row) row.insertAdjacentHTML('beforeend', `<span class="fc-inst__cta">${strings.cta ? `<span>${esc(strings.cta)}</span>` : ''}${SVG_CHEVRON}</span>`);
    const x = panel.querySelector('[data-fc-close]');
    if (to !== 'popup' && x) x.remove();
    const head = panel.querySelector('.fc-inst__head');
    if (to === 'popup' && !x && head) head.insertAdjacentHTML('beforeend', `<button class="fc-inst__x" type="button" data-fc-close aria-label="${esc(closeLabel || 'Close')}">${SVG_CLOSE}</button>`);
    if (to === 'popup') {
      const d = doc.createElement('dialog');
      d.className = 'fc-inst__dialog';
      const h = panel.querySelector('.fc-inst__h');
      if (h) d.setAttribute('aria-label', h.textContent.trim());
      panel.replaceWith(d);
      d.appendChild(panel);
    } else if (to === 'inline') {
      const w = doc.createElement('div');
      w.className = 'fc-inst__wrap';
      const inner = doc.createElement('div');
      w.appendChild(inner);
      panel.replaceWith(w);
      inner.appendChild(panel);
    }
  }

  class FcCartInstallments extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      /* a product widget printed later (quick view, theme editor re-render) refreshes the copy */
      document.addEventListener('fc-installments:template', () => { if (PRISTINE) this.mount(PRISTINE.html); }, { signal: this.abort.signal });
      const known = PRISTINE || readCache();
      if (known) this.mount(known.html);
      if (PRISTINE || (known && Date.now() - known.t < FRESH_MS)) return;
      const handle = this.dataset.handle;
      if (!handle) { if (!known) this.fail(); return; }
      fetchTemplate(handle, known && known.section).then((rec) => {
        if (!this.isConnected) return;
        if (rec) this.mount(rec.html); else if (!known) this.fail();
      });
    }
    disconnectedCallback() { this.abort.abort(); }
    fail() {
      const hint = this.querySelector('[data-fc-cart-hint]');
      if (hint) hint.hidden = false;
    }
    mount(html) {
      const sig = signature(html);
      if (sig === this.sig && this.querySelector('fc-installments')) return;
      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      const el = tpl.content.querySelector('fc-installments');
      if (!el) return;
      this.adapt(el);
      this.sig = sig;
      const old = this.querySelector('fc-installments');
      if (old) old.replaceWith(el); else this.appendChild(el);
      const hint = this.querySelector('[data-fc-cart-hint]');
      if (hint) hint.hidden = true;
    }
    adapt(el) {
      const d = this.dataset;
      const ctx = d.context || 'cart';
      let cfg = {};
      try { cfg = JSON.parse(el.getAttribute('data-config') || '{}'); } catch (e) { cfg = {}; }
      const was = cfg.layout || 'popup';
      const layout = ['popup', 'inline', 'open'].includes(d.layout) ? d.layout : was;
      cfg.layout = layout;
      if (d.logos === 'false') cfg.show_logos = false;
      el.setAttribute('data-config', JSON.stringify(cfg));
      el.setAttribute('data-mode', 'cart');
      el.setAttribute('data-context', ctx);
      el.setAttribute('data-price', d.total || '0');
      el.removeAttribute('data-form');
      el.removeAttribute('data-section');
      el.id = `fc-inst-cart-${ctx}`;
      /* the copy must never answer the product block's theme-editor events */
      [el, ...el.querySelectorAll('[data-shopify-editor-block]')].forEach((n) => n.removeAttribute('data-shopify-editor-block'));
      el.querySelectorAll('[data-fc-hint]').forEach((n) => n.remove());
      el.classList.remove('hdt-product-info__item', 'fc-inst--popup', 'fc-inst--inline', 'fc-inst--open');
      el.classList.add(`fc-inst--${layout}`, 'fc-inst--cart', `fc-inst--cart-${ctx}`);
      if (d.mt) el.style.setProperty('--fc-mt', `${parseInt(d.mt, 10) || 0}px`);
      if (d.mb) el.style.setProperty('--fc-mb', `${parseInt(d.mb, 10) || 0}px`);
      if (d.logos === 'false') el.querySelectorAll('[data-fc-logos]').forEach((n) => n.remove());
      if (layout !== was) relayout(el, layout, cfg.strings || {}, d.close);
      /* "Product price" -> "Cart total", and a note that shipping comes on top */
      const price = el.querySelector('.fc-inst__price');
      const amount = price && price.querySelector('[data-fc-price]');
      if (amount && d.label) { price.textContent = `${d.label} `; price.appendChild(amount); }
      const panel = el.querySelector('.fc-inst__panel');
      if (!panel) return;
      panel.id = `fc-inst-panel-cart-${ctx}`;
      el.querySelectorAll('[data-fc-open]').forEach((b) => b.setAttribute('aria-controls', panel.id));
      if (d.note) {
        const note = el.ownerDocument.createElement('div');
        note.className = 'fc-inst__foot fc-inst__foot--cart';
        note.textContent = d.note;
        const foot = panel.querySelector('.fc-inst__foot');
        if (foot) foot.before(note); else panel.appendChild(note);
      }
    }
  }
  customElements.define('fc-cart-installments', FcCartInstallments);
})();
